import {
  CONTRACT_KINDS,
  CONTRACT_STATUSES,
  formatDate,
  formatUah,
  fullName,
  type ContractKind,
  type ContractStatus,
} from '@hr/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { FileSignatureIcon, FileTextIcon } from 'lucide-react';
import { useMemo } from 'react';
import { z } from 'zod';
import { Combobox } from '@/components/combobox';
import { DataTable } from '@/components/data-table';
import { EmptyState, PageHeader } from '@/components/page';
import { SearchInput } from '@/components/search-input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ContractStatusBadge } from '@/features/personnel/status';
import { usePreview } from '@/features/preview/use-preview';
import { SignedCopy } from '@/features/signed/signed-copy';
import { useRegistry } from '@/features/registry/use-registry';
import { CONTRACT_KIND_LABELS, CONTRACT_STATUS_LABELS, RATE_UNIT_LABELS, uk } from '@/i18n/uk';
import { listSearchShape, tableState, tableStateToSearch } from '@/lib/search-params';
import { trpc } from '@/lib/trpc';
import type { ContractListItem } from '@/lib/types';

const searchSchema = z.object({
  ...listSearchShape,
  status: z.enum(CONTRACT_STATUSES).optional().catch(undefined),
  kind: z.enum(CONTRACT_KINDS).optional().catch(undefined),
  project: z.uuid().optional().catch(undefined),
  expiring: z.boolean().optional().catch(undefined),
});

export const Route = createFileRoute('/_app/contracts')({
  validateSearch: searchSchema,
  component: ContractsPage,
});

function ContractsPage() {
  const t = uk.personnel;
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const registry = useRegistry();
  const openPreview = usePreview();
  const state = tableState(search, { sort: 'startsAt', dir: 'desc' });
  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const list = useQuery({
    ...trpc.contracts.list.queryOptions({
      page: state.page,
      pageSize: state.pageSize,
      sort: state.sort,
      dir: state.dir,
      q: search.q,
      status: search.status,
      kind: search.kind,
      projectId: search.project,
      expiringDays: search.expiring ? 30 : undefined,
    }),
    placeholderData: keepPreviousData,
  });
  const columns = useMemo<ColumnDef<ContractListItem>[]>(
    () => [
      {
        id: 'number',
        header: t.number,
        cell: ({ row }) => <span className="font-medium">{row.original.number}</span>,
      },
      {
        id: 'employee',
        header: t.employee,
        cell: ({ row }) => fullName(row.original),
        meta: { className: 'min-w-48' },
      },
      {
        id: 'kind',
        header: t.kind,
        enableSorting: false,
        cell: ({ row }) => CONTRACT_KIND_LABELS[row.original.kind],
      },
      {
        id: 'project',
        header: uk.registry.project,
        cell: ({ row }) =>
          [row.original.projectCode, row.original.positionTitle].filter(Boolean).join(' · '),
      },
      {
        id: 'startsAt',
        header: uk.registry.period,
        cell: ({ row }) => (
          <span className="whitespace-nowrap">
            {formatDate(row.original.startsAt)} – {formatDate(row.original.endsAt) || '…'}
          </span>
        ),
      },
      {
        id: 'rate',
        header: t.rate,
        enableSorting: false,
        cell: ({ row }) => (
          <span className="whitespace-nowrap tabular-nums">
            {formatUah(row.original.rate)}{' '}
            <span className="text-xs text-muted-foreground">
              {RATE_UNIT_LABELS[row.original.rateUnit]}
            </span>
          </span>
        ),
        meta: { className: 'text-right' },
      },
      {
        id: 'status',
        header: t.status,
        cell: ({ row }) => <ContractStatusBadge status={row.original.status} />,
      },
      {
        id: 'doc',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label={t.downloadDocx}
            onClick={(e) => {
              e.stopPropagation();
              openPreview({ url: `/api/contracts/${row.original.id}/document.docx` });
            }}
          >
            <FileTextIcon />
          </Button>
        ),
      },
      {
        id: 'signed',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <SignedCopy
            kind="contract"
            entityId={row.original.id}
            title={row.original.number}
            compact
          />
        ),
      },
    ],
    [t, openPreview],
  );
  return (
    <>
      <PageHeader title={t.contracts} />
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <SearchInput
          value={search.q ?? ''}
          placeholder={`${t.number}, ${t.fullName}…`}
          onChange={(q) => setSearch({ q: q || undefined, page: undefined })}
        />
        <Select
          value={search.status ?? 'all'}
          onValueChange={(v) =>
            setSearch({ status: v === 'all' ? undefined : (v as ContractStatus), page: undefined })
          }
        >
          <SelectTrigger className="w-40" aria-label={t.status}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t.status}: {uk.common.all.toLowerCase()}
            </SelectItem>
            {CONTRACT_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {CONTRACT_STATUS_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={search.kind ?? 'all'}
          onValueChange={(v) =>
            setSearch({ kind: v === 'all' ? undefined : (v as ContractKind), page: undefined })
          }
        >
          <SelectTrigger className="w-48" aria-label={t.kind}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t.kind}: {uk.common.all.toLowerCase()}
            </SelectItem>
            {CONTRACT_KINDS.map((k) => (
              <SelectItem key={k} value={k}>
                {CONTRACT_KIND_LABELS[k]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Combobox
          className="w-44"
          value={search.project}
          onChange={(v) => setSearch({ project: v ?? undefined, page: undefined })}
          options={(registry.data?.projects ?? []).map((p) => ({
            value: p.id,
            label: p.code,
            hint: p.name,
          }))}
          placeholder={uk.registry.project}
          allowClear
        />
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={Boolean(search.expiring)}
            onCheckedChange={(v) =>
              setSearch({ expiring: v === true || undefined, page: undefined })
            }
          />
          {t.expiring}
        </label>
      </div>
      <DataTable
        columns={columns}
        data={list.data?.items}
        total={list.data?.total ?? 0}
        state={state}
        onStateChange={(next) => setSearch(tableStateToSearch(next))}
        isLoading={list.isPending}
        getRowId={(r) => r.id}
        onRowClick={(r) =>
          void navigate({
            to: '/employees/$employeeId',
            params: { employeeId: r.employeeId },
            search: { tab: 'contracts' },
          })
        }
        empty={<EmptyState icon={FileSignatureIcon} title={t.noContracts} />}
      />
    </>
  );
}
